/**
 * Merging the device REGISTRY into the live-state device lists.
 *
 * The console's door and lift lists were built entirely from live
 * state, which made two completely different situations look
 * identical: "this building has no such device" and "the thing that
 * publishes this device's state is dead". At sg-nomad-01 the MEGAZO
 * door adapter's vendor login was failing, so
 * every `rmf/v1/sites/sg-nomad-01/doors/<name>/state` key was absent and the
 * Doors tab was empty -- while `doors_registry`, derived from the
 * building map, carried `L4_SLL_Door` the whole time. During
 * commissioning that is backwards: the registry is the building's real
 * inventory, and the missing state is precisely the thing an engineer
 * has come to see.
 *
 * So the registry decides MEMBERSHIP and live state decides CONTENT. A
 * device that publishes state is passed through untouched -- these
 * functions add nothing to it and take nothing away, because the whole
 * point is that a working device's row does not change. The one exception
 * is a door's floor: `DoorState` carries none, so a live door would have
 * no floor at all, and the floor is the registry's fact either way. A
 * device that
 * only the registry knows about is appended with its registry-known
 * fields and an explicitly unknown state, and carries
 * `hasLiveState: false` so the UI can refuse to command it.
 *
 * Nothing here invents a reading. Fields the registry cannot supply
 * (a door's second endpoint, a lift's current floor) are left undefined
 * rather than derived, because a plausible-looking wrong value on a
 * commissioning screen is worse than a dash.
 */
import type { DoorRegistry, LiftRegistry } from './v1';
import type { DoorViewModel, LiftViewModel } from './adapters';

/**
 * One `doors_registry` entry as a row with no state behind it.
 *
 * `doorType` stays undefined on purpose: the registry says `kind`
 * ('single'/'double'), which is not the numeric `door_type` bit-flag
 * from `rmf_door_msgs/Door.msg` that the drawer's label table is keyed
 * on. Mapping one onto the other would be a guess, and "hinged" shown
 * for a door nobody has described is a lie an engineer might act on.
 *
 * `v2` stays undefined for the same reason: the registry carries one
 * endpoint plus a yaw and a width, and reconstructing the far endpoint
 * from them assumes a convention this SPA has never verified against
 * the map exporter. A door drawn in the wrong place is worse than a
 * door drawn from the building map alone, which is where the map
 * screen already gets its geometry.
 */
export function adaptDoorRegistryEntry(r: DoorRegistry): DoorViewModel {
  return {
    id: r.id.door,
    name: r.id.door,
    level: r.level,
    state: 'unknown',
    v1: { x: r.v1_x, y: r.v1_y },
    siteId: r.id.site,
    // The field this whole module exists to set.
    hasLiveState: false,
  };
}

/**
 * One `lifts_registry` entry as a row with no state behind it.
 *
 * `currentLevel` is deliberately NOT taken from the registry's `level`.
 * That field is where the map places the lift, not where the car is;
 * rendering it under "Current Floor" would put a floor number on a car
 * nothing has heard from, which is the exact class of confident-wrong
 * reading this change exists to remove.
 *
 * `mode` and `fault` stay undefined rather than becoming 'unknown' and
 * false: undefined is "nobody said", while `mode: 'unknown'` is a car
 * that reported MODE_UNKNOWN, and `liftDispatchStatus` reads the pair
 * of them to tell an emergency from an unreported mode.
 */
export function adaptLiftRegistryEntry(r: LiftRegistry): LiftViewModel {
  return {
    id: r.id.lift,
    name: r.id.lift,
    levels: r.levels,
    motionState: 'unknown',
    doorState: 'unknown',
    ref: { x: r.v1_x, y: r.v1_y },
    width: r.door_width_m,
    depth: r.door_height_m,
    siteId: r.id.site,
    hasLiveState: false,
  };
}

/**
 * Live rows first and unchanged, then whatever the registry knows about
 * and the live snapshot does not.
 *
 * Keyed on `name`, which is the same key `useLiveData`'s `mergeById`
 * overlay uses for this list, so a device present in both appears
 * exactly once and it is the LIVE row that wins.
 *
 * Order matters as an ordering, not as a ranking: the tables here are
 * sortable and an operator will sort them, but appending rather than
 * interleaving keeps the untouched live rows at the indices they have
 * today.
 */
export function mergeDoorsWithRegistry(
  live: readonly DoorViewModel[] | undefined,
  registry: readonly DoorRegistry[] | undefined,
): DoorViewModel[] {
  if (!registry || registry.length === 0) return live ? [...live] : [];
  const floors = new Map(registry.map((r) => [r?.id?.door, r?.level]));
  const rows: DoorViewModel[] = (live ?? []).map((d) => {
    const floor = d.level ? undefined : floors.get(d.name);
    return floor ? { ...d, level: floor } : d;
  });
  const seen = new Set(rows.map((d) => d.name));
  for (const r of registry) {
    const name = r?.id?.door;
    // `seen` is added to as we go, so a registry that lists the same
    // door twice still yields one row.
    if (!name || seen.has(name)) continue;
    seen.add(name);
    rows.push(adaptDoorRegistryEntry(r));
  }
  return rows;
}

/** The lifts half of `mergeDoorsWithRegistry`, rule for rule — the two
 *  device lists must follow the same one or an operator learns a
 *  different lesson from each. */
export function mergeLiftsWithRegistry(
  live: readonly LiftViewModel[] | undefined,
  registry: readonly LiftRegistry[] | undefined,
): LiftViewModel[] {
  const rows: LiftViewModel[] = live ? [...live] : [];
  if (!registry || registry.length === 0) return rows;
  const seen = new Set(rows.map((l) => l.name));
  for (const r of registry) {
    const name = r?.id?.lift;
    if (!name || seen.has(name)) continue;
    seen.add(name);
    rows.push(adaptLiftRegistryEntry(r));
  }
  return rows;
}
